import { useEffect } from 'react';
import { useCms } from '../context/CmsContext';
import { AppPageId } from '../App';
import { DEFAULT_SEO_SETTINGS } from '../data/defaultSeoData';
import { DEFAULT_PRODUCT_CATALOGUE } from '../data/defaultProductsData';
import { INDUSTRY_SECTORS } from '../data/simatechData';

type PageKey = 'home' | 'about' | 'services' | 'industry' | 'product' | 'clients' | 'contact' | 'siemens-solution-partner';

interface SeoHeadProps {
  currentPage: AppPageId;
  productCategorySlug?: string;
  industrySectorSlug?: string;
}

export function SeoHead({ currentPage, productCategorySlug, industrySectorSlug }: SeoHeadProps) {
  const { siteData } = useCms();
  const seo = siteData?.seo || DEFAULT_SEO_SETTINGS;
  const brand = siteData?.brand;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // 1. Handle Admin Page
    if (currentPage === 'admin') {
      document.title = 'Simatech CMS | Admin Control Center';
      setOrCreateMeta('name', 'robots', 'noindex, nofollow');
      return;
    }

    // 2. Resolve Page-Specific SEO Configuration
    const pageKey: PageKey = (['home', 'about', 'services', 'industry', 'product', 'clients', 'contact', 'siemens-solution-partner'].includes(currentPage) ? currentPage : 'home') as PageKey;
    const pageConfig = seo.pages?.[pageKey] || DEFAULT_SEO_SETTINGS.pages[pageKey] || DEFAULT_SEO_SETTINGS.pages.home;

    const baseCanonical = (seo.canonicalBaseUrl || 'https://simatechautomation.com').replace(/\/+$/, '');
    const pagePath = pageKey === 'home' ? '' : `/${pageKey}/`;
    let canonicalUrl = pageConfig.canonicalUrl || `${baseCanonical}${pagePath}`;

    // 3. Document Title & Description Formatting (with Deep Product Detail SEO Support)
    const rawTitle = pageConfig.title || seo.defaultTitle;
    let finalTitle = rawTitle;
    if (pageKey !== 'home' && seo.siteTitleTemplate && seo.siteTitleTemplate.includes('%s')) {
      finalTitle = seo.siteTitleTemplate.replace('%s', rawTitle.split('|')[0].trim());
    }

    let description = pageConfig.description || seo.defaultDescription;
    let keywords = pageConfig.keywords || seo.defaultKeywords;
    let productOgImg = '';

    if (currentPage === 'product' && productCategorySlug) {
      const allProducts = siteData?.pages?.product?.items || DEFAULT_PRODUCT_CATALOGUE;
      const foundProduct = allProducts.find((p: any) => p.id === productCategorySlug);
      if (foundProduct) {
        finalTitle = `${foundProduct.title} | Siemens Automation | Simatech Automation`;
        canonicalUrl = `${baseCanonical}/product/${foundProduct.id}/`;
        description = foundProduct.editorialSubtitle || foundProduct.paragraphs?.[0] || description;
        keywords = `${foundProduct.title}, Siemens ${foundProduct.title}, Authorized Siemens System House, Simatech Automation, ${foundProduct.subTitle}`;
        productOgImg = foundProduct.imageLeft || foundProduct.image || '';
      }
    }

    if (currentPage === 'industry' && industrySectorSlug) {
      const sector = INDUSTRY_SECTORS.find((s) => s.id === industrySectorSlug);
      if (sector) {
        finalTitle = `${sector.sector} Automation | Industrial PLC & SCADA | Simatech Automation`;
        canonicalUrl = `${baseCanonical}/industry/${sector.id}/`;
        description = `${sector.scope} Simatech Automation delivers Siemens PLC, SCADA, and VFD-based turnkey automation for the ${sector.sector} industry.`;
        keywords = `${sector.sector} automation, ${sector.sector} PLC SCADA, Siemens ${sector.sector}, industrial automation ${sector.sector}, Simatech Automation`;
      }
    }
    document.title = finalTitle;

    // 4. Standard Metadata
    const robots = pageConfig.robots || seo.robotsDefault || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

    setOrCreateMeta('name', 'description', description);
    setOrCreateMeta('name', 'keywords', keywords);
    setOrCreateMeta('name', 'robots', robots);

    // 5. Canonical Link Tag
    let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // 6. Open Graph Tags
    const ogTitle = pageConfig.ogTitle || finalTitle;
    const ogDesc = pageConfig.ogDescription || description;
    const ogImg = (productOgImg ? (productOgImg.startsWith('http') ? productOgImg : `${baseCanonical}${productOgImg}`) : (pageConfig.ogImage || seo.defaultOgImage || `${baseCanonical}/simatech_logo.png`));

    setOrCreateMeta('property', 'og:type', 'website');
    setOrCreateMeta('property', 'og:url', canonicalUrl);
    setOrCreateMeta('property', 'og:title', ogTitle);
    setOrCreateMeta('property', 'og:description', ogDesc);
    setOrCreateMeta('property', 'og:image', ogImg);
    setOrCreateMeta('property', 'og:site_name', seo.organizationName || brand?.name || 'Simatech Automation');
    setOrCreateMeta('property', 'og:locale', 'en_US');

    // 7. Twitter Card Tags
    setOrCreateMeta('name', 'twitter:card', seo.defaultTwitterCard || 'summary_large_image');
    setOrCreateMeta('name', 'twitter:url', canonicalUrl);
    setOrCreateMeta('name', 'twitter:title', ogTitle);
    setOrCreateMeta('name', 'twitter:description', ogDesc);
    setOrCreateMeta('name', 'twitter:image', ogImg);

    // 8. Search Engine Webmaster Verification
    if (seo.googleSiteVerification) {
      setOrCreateMeta('name', 'google-site-verification', seo.googleSiteVerification);
    }
    if (seo.bingSiteVerification) {
      setOrCreateMeta('name', 'msvalidate.01', seo.bingSiteVerification);
    }

    // 9. Structured Data JSON-LD Graph Injection
    const schemaGraph = buildSchemaGraph(pageKey, finalTitle, description, canonicalUrl, seo, brand);
    let scriptTag = document.getElementById('simatech-seo-schema') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'simatech-seo-schema';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schemaGraph, null, 2);

  }, [currentPage, productCategorySlug, industrySectorSlug, seo, brand]);

  return null;
}

// Helper: Set or create meta tag
function setOrCreateMeta(attributeName: 'name' | 'property', attributeValue: string, contentValue: string) {
  if (!contentValue) return;
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attributeName}="${attributeValue}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', contentValue);
}

// Helper: Build comprehensive Schema.org JSON-LD Graph
function buildSchemaGraph(
  pageKey: PageKey,
  title: string,
  description: string,
  canonicalUrl: string,
  seo: any,
  brand: any
) {
  const baseCanonical = (seo.canonicalBaseUrl || 'https://simatechautomation.com').replace(/\/+$/, '');
  const orgName = seo.organizationName || brand?.name || 'Simatech Automation';
  const orgLegalName = seo.organizationLegalName || 'Simatech Automation';
  const orgLogo = seo.defaultOgImage || `${baseCanonical}/simatech_logo.png`;

  const orgNode = {
    '@type': seo.organizationType || 'Organization',
    '@id': `${baseCanonical}/#organization`,
    name: orgName,
    legalName: orgLegalName,
    url: baseCanonical,
    logo: orgLogo,
    image: orgLogo,
    description: seo.defaultDescription,
    telephone: seo.telephone || brand?.phones?.[0] || '+91-9820234033',
    email: seo.email || brand?.emails?.[0] || 'info@simatechautomation.com',
    foundingDate: seo.foundingYear || brand?.establishedYear || '2006',
    address: {
      '@type': 'PostalAddress',
      streetAddress: seo.streetAddress || brand?.address?.street || 'Gala No 1, 2, 3, 4, 1st Floor, Parmar Industrial Estate',
      addressLocality: seo.addressLocality || brand?.address?.area || 'Vasai East, Thane',
      addressRegion: seo.addressRegion || 'Maharashtra',
      postalCode: seo.postalCode || '401208',
      addressCountry: seo.addressCountry || 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: seo.geoLatitude || '19.3872',
      longitude: seo.geoLongitude || '72.8361',
    },
    sameAs: seo.socialSameAs || [
      'https://www.facebook.com/simatechautomation/',
      'https://www.instagram.com/simatechautomation/',
    ],
  };

  const websiteNode = {
    '@type': 'WebSite',
    '@id': `${baseCanonical}/#website`,
    url: baseCanonical,
    name: orgName,
    publisher: { '@id': `${baseCanonical}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseCanonical}/product?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const breadcrumbNode = {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseCanonical,
      },
      ...(pageKey !== 'home'
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: pageKey.charAt(0).toUpperCase() + pageKey.slice(1),
              item: canonicalUrl,
            },
          ]
        : []),
    ],
  };

  const webpageNode: any = {
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: title,
    description: description,
    isPartOf: { '@id': `${baseCanonical}/#website` },
    breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
    inLanguage: 'en-US',
  };

  const graph: any[] = [orgNode, websiteNode, breadcrumbNode, webpageNode];

  // Specific Rich Snippets for Services
  if (pageKey === 'services') {
    graph.push({
      '@type': 'Service',
      serviceType: 'Turnkey Industrial Automation & Panel Manufacturing',
      provider: { '@id': `${baseCanonical}/#organization` },
      areaServed: ['India', 'Global', 'United States', 'Germany', 'UAE', 'Saudi Arabia'],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Industrial Automation Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'PLC & SCADA Software Architecture' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Electrical Control Panel Fabrication' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Multi-Axis VFD & Servo Synchronization' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '24/7 AMC Breakdown Plant Support' } },
        ],
      },
    });
  }

  // Specific Rich Snippets for Products
  if (pageKey === 'product') {
    graph.push({
      '@type': 'OfferCatalog',
      name: 'Siemens Industrial Automation Hardware & Solutions',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Siemens SIMATIC S7-1500 & S7-1200 Advanced PLC Integration',
            description: 'Engineering, TIA Portal software architecture, and plant commissioning for Siemens SIMATIC PLCs.',
          },
          seller: { '@type': 'Organization', name: orgName },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Siemens SINAMICS VFD & Multi-Axis Servo Synchronization',
            description: 'Engineering and supply of Siemens SINAMICS variable frequency and servo drives with SIL 3 safety.',
          },
          seller: { '@type': 'Organization', name: orgName },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Siemens SIMATIC ET-200SP Distributed I/O Engineering',
            description: 'Modular decentralized periphery integration with PROFINET optical/copper interfaces.',
          },
          seller: { '@type': 'Organization', name: orgName },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Siemens SIMATIC HMI Touchscreen Architecture',
            description: 'Comfort and Unified touch panel programming conforming to 21 CFR Part 11 audit trails.',
          },
          seller: { '@type': 'Organization', name: orgName },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Siemens SIMATIC WinCC SCADA Architecture',
            description: 'Enterprise process visualization, historian database integration, and remote telemetry on IPCs.',
          },
          seller: { '@type': 'Organization', name: orgName },
        },
      ],
    });
  }

  // Specific Rich Snippets for Contact Page
  if (pageKey === 'contact') {
    graph.push({
      '@type': 'ContactPage',
      '@id': `${canonicalUrl}#contactpage`,
      url: canonicalUrl,
      name: 'Contact Simatech Automation',
      mainEntity: { '@id': `${baseCanonical}/#organization` },
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
